import React, { useEffect, useRef, useCallback, useState } from "react";
import { format } from "date-fns";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Upload, X, FileText, Image, File, Calendar, DollarSign, User, MessageSquare, Info, AlertCircle, CheckCircle, Plus, Search } from "lucide-react";
import Select from "react-select";
import axios from "../../../../services/AxiosInstance";
import { getApiUrl } from "../../../../utils/ApiUtils";
import { toast } from "react-toastify";

// Filter the tenant accounts list by a new-shape input.filter
// expression. Supports `class:N`, `class:N-M`, `class:1,2,3`.
// Unknown / missing filter → return the full list unchanged.
const filterAccountsByExpression = (accounts, filter) => {
  if (!Array.isArray(accounts) || accounts.length === 0) return [];
  if (!filter || typeof filter !== 'string') return accounts;
  const m = /^class:(.+)$/.exec(filter.trim());
  if (!m) return accounts;
  const spec = m[1].trim();
  const firstDigit = (acct) => {
    const s = (acct && (acct.accountNumber || acct.value)) || '';
    return typeof s === 'string' && s.length > 0 ? s[0] : '';
  };
  if (/^\d(\s*,\s*\d)+$/.test(spec)) {
    const set = new Set(spec.split(',').map((s) => s.trim()));
    return accounts.filter((a) => set.has(firstDigit(a)));
  }
  const range = /^(\d)\s*-\s*(\d)$/.exec(spec);
  if (range) {
    const lo = parseInt(range[1], 10);
    const hi = parseInt(range[2], 10);
    return accounts.filter((a) => {
      const d = parseInt(firstDigit(a), 10);
      return Number.isFinite(d) && d >= lo && d <= hi;
    });
  }
  if (/^\d$/.test(spec)) {
    return accounts.filter((a) => firstDigit(a) === spec);
  }
  return accounts;
};

// One-line VAT-rate hint shown under currency_gross/_net inputs when
// they declare a literal vat_rate. Keeps the description's
// Skatteverket grounding visible inline.
const vatRateHint = (input) => {
  if (!input || typeof input.vat_rate !== 'number') return null;
  if (input.vat_rate === 25) return 'Standardmoms — varor och tjänster i allmänhet';
  if (input.vat_rate === 12) return 'Reducerad moms — livsmedel, hotell, restaurang';
  if (input.vat_rate === 6)  return 'Reducerad moms — böcker, persontrafik, kultur';
  if (input.vat_rate === 0)  return 'Momsfritt';
  return null;
};

const DetailsEntryStep = ({
  entry,
  onChange,
  companyId,
  accounts = [],
  accountsLoading = false,
  accountsError = null,
  getAccountByNumber,
  getAccountsByType,
  searchAccounts
}) => {
  const formikRef = useRef();
  const fileInputRef = useRef();
  const [attachments, setAttachments] = useState(entry.attachments || []);
  const [isDragOver, setIsDragOver] = useState(false);
  const [completedSections, setCompletedSections] = useState({});
  const [uploadingFiles, setUploadingFiles] = useState(new Set());

  // Hover preview states
  const [hoveredAttachment, setHoveredAttachment] = useState(null);
  const [previewPosition, setPreviewPosition] = useState({ x: 0, y: 0 });
  const [previewBlobUrl, setPreviewBlobUrl] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [textContent, setTextContent] = useState(null);

  const maxFiles = 10; // Maximum number of files allowed

  useEffect(() => {
    if (entry.attachments && entry.attachments !== attachments) {
      setAttachments(entry.attachments);
    }
  }, [entry.attachments]);

  const handleFileUpload = async (files) => {
    const fileArray = Array.from(files);

    // Check if adding these files would exceed the limit
    if (attachments.length + fileArray.length > maxFiles) {
      toast.error(`Du kan bara ladda upp max ${maxFiles} filer. Du har ${attachments.length} och försöker lägga till ${fileArray.length}.`);
      return;
    }

    // Validate files before upload
    const validFiles = fileArray.filter(file => {
      // File size validation (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} är för stor. Maximal storlek är 10MB.`);
        return false;
      }

      // File type validation
      const allowedTypes = [
        'image/jpeg', 'image/jpg', 'image/png',
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'text/plain'
      ];

      if (!allowedTypes.includes(file.type)) {
        toast.error(`${file.name} har en filtyp som inte stöds.`);
        return false;
      }

      // Check for duplicates
      if (attachments.some(att => att.name === file.name && att.size === file.size)) {
        toast.error(`${file.name} är redan uppladdad.`);
        return false;
      }

      return true;
    });

    if (validFiles.length === 0) return;

    // Track uploading files
    const newUploadingFiles = new Set(uploadingFiles);
    validFiles.forEach(file => newUploadingFiles.add(file.name));
    setUploadingFiles(newUploadingFiles);

    const uploadPromises = validFiles.map(async (file) => {
      const formData = new FormData();
      formData.append('file', file);

      try {
        const uploadFileApiUrl = getApiUrl(`${companyId}/accounting/verifications/attachments`);

        const response = await axios.post(
          uploadFileApiUrl,
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
            withCredentials: true
          }
        );

        if (response.data.success) {
          return {
            id: response.data.attachment.attachmentId,
            name: response.data.attachment.fileName,
            size: response.data.attachment.fileSize,
            type: response.data.attachment.fileType,
            url: response.data.attachment.fileUrl,
            blob_name: response.data.attachment.blobName,
            preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
            attachmentId: response.data.attachment.attachmentId,
            blobName: response.data.attachment.blobName
          };
        } else {
          throw new Error(response.data.message || 'Uppladdning misslyckades');
        }
      } catch (error) {
        console.error(`Failed to upload ${file.name}:`, error);
        toast.error(`Misslyckades att ladda upp ${file.name}: ${error.response?.data?.message || error.message}`);
        return null;
      } finally {
        // Remove from uploading set
        setUploadingFiles(prev => {
          const newSet = new Set(prev);
          newSet.delete(file.name);
          return newSet;
        });
      }
    });

    try {
      const uploadResults = await Promise.all(uploadPromises);
      const successfulUploads = uploadResults.filter(result => result !== null);

      if (successfulUploads.length > 0) {
        const newAttachments = [...attachments, ...successfulUploads];
        setAttachments(newAttachments);

        onChange(prev => ({
          ...prev,
          attachments: newAttachments
        }));

        toast.success(`${successfulUploads.length} fil(er) uppladdade framgångsrikt`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Vissa filer kunde inte laddas upp');
    }
  };

  const removeAttachment = async (attachmentId) => {

    try {
      // Call backend to delete attachment
      const result = await axios.delete(getApiUrl(`${companyId}/accounting/verifications/attachments/${attachmentId}`), {
        withCredentials: true
      });

      const newAttachments = attachments.filter(att => att.id !== attachmentId);
      setAttachments(newAttachments);

      // Clean up preview URL if it exists
      const attachment = attachments.find(att => att.id !== attachmentId);
      if (attachment && attachment.preview) {
        URL.revokeObjectURL(attachment.preview);
      }

      onChange(prev => ({
        ...prev,
        attachments: newAttachments
      }));

      toast.success('Bilaga borttagen');
    } catch (error) {
      console.error('Error removing attachment:', error);
      toast.error("Kunde inte ta bort bilagan. Försök igen.");
    }
  };

  // Helper function to check if file can be previewed
  const canPreviewFile = (fileName, fileType) => {
    if (!fileName && !fileType) return false;

    // Check by MIME type first
    if (fileType) {
      if (fileType.startsWith('image/') || fileType === 'application/pdf' || fileType.startsWith('text/')) {
        return true;
      }
    }

    // Fallback to file extension check
    if (fileName) {
      const extension = fileName.toLowerCase().split('.').pop();
      const previewableExtensions = ['jpg', 'jpeg', 'png', 'gif', 'pdf', 'txt', 'csv', 'json', 'xml'];
      return previewableExtensions.includes(extension);
    }

    return false;
  };

  // Function to fetch attachment blob for preview
  const fetchAttachmentBlob = async (attachment) => {
    if (!attachment) return null;

    // Check if file can be previewed before attempting fetch
    if (!canPreviewFile(attachment.name, attachment.type)) {
      console.log('File cannot be previewed based on type/extension:', attachment.name, attachment.type);
      return null;
    }

    try {
      setLoadingPreview(true);
      console.log('Fetching attachment blob for:', attachment.name);
      console.log('Using attachment ID:', attachment.attachmentId || attachment.id);

      const response = await axios.get(
        getApiUrl(`${companyId}/attachments/${attachment.attachmentId || attachment.id}`),
        {
          responseType: 'blob',
          withCredentials: true,
          timeout: 10000,
        }
      );

      // Validate that we actually got a blob
      if (response.data && response.data.size > 0) {
        const blobUrl = URL.createObjectURL(response.data);
        console.log('Created blob URL:', blobUrl);
        return blobUrl;
      } else {
        console.warn('Received empty blob for attachment:', attachment.name);
        return null;
      }
    } catch (error) {
      console.error('Error fetching attachment blob:', error);
      return null;
    } finally {
      setLoadingPreview(false);
    }
  };

  // Function to fetch text content for text files
  const fetchTextContent = async (attachment) => {
    if (!attachment) return null;

    // Check if this is a text file by type or extension
    const isTextFile = attachment.type?.startsWith('text/') ||
      ['txt', 'csv', 'json', 'xml'].includes(
        attachment.name?.toLowerCase().split('.').pop()
      );

    if (!isTextFile) return null;

    try {
      setLoadingPreview(true);
      console.log('Fetching text content for:', attachment.name);

      const response = await axios.get(
        getApiUrl(`${companyId}/attachments/${attachment.attachmentId || attachment.id}`),
        {
          responseType: 'text',
          withCredentials: true,
          timeout: 10000,
        }
      );

      console.log('Text content fetched, length:', response.data?.length);
      return response.data;
    } catch (error) {
      console.error('Error fetching text content:', error);
      return null;
    } finally {
      setLoadingPreview(false);
    }
  };

  // Enhanced drag and drop with better UX
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    // Only remove drag state if leaving the drop zone entirely
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragOver(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload(files);
    }
  };

  // Hover preview handlers
  const handleAttachmentHover = async (attachment, event) => {
    console.log('Hovering attachment:', attachment);

    const rect = event.currentTarget.getBoundingClientRect();
    setPreviewPosition({
      x: rect.left + rect.width + 10,
      y: rect.top
    });
    setHoveredAttachment(attachment);

    // Clean up previous blob URL and text content
    if (previewBlobUrl) {
      URL.revokeObjectURL(previewBlobUrl);
      setPreviewBlobUrl(null);
    }
    setTextContent(null);
    setLoadingPreview(false); // Reset loading state

    try {
      // Fetch appropriate preview content based on file type
      if (attachment.type?.startsWith('image/') || attachment.type === 'application/pdf') {
        const blobUrl = await fetchAttachmentBlob(attachment);
        if (blobUrl) {
          setPreviewBlobUrl(blobUrl);
        } else if (attachment.preview && attachment.type?.startsWith('image/')) {
          // Fallback to existing preview only for images
          setPreviewBlobUrl(attachment.preview);
        }
      } else if (attachment.type?.startsWith('text/')) {
        const content = await fetchTextContent(attachment);
        if (content) {
          setTextContent(content);
        }
      }
    } catch (error) {
      console.error('Error in attachment hover handler:', error);
      // Ensure loading state is cleared even if something goes wrong
      setLoadingPreview(false);
    }
  };

  const handleAttachmentLeave = () => {
    console.log('Leaving attachment hover');
    setHoveredAttachment(null);
    setLoadingPreview(false); // Ensure loading state is cleared

    // Clean up blob URL and text content
    if (previewBlobUrl && !attachments.find(att => att.preview === previewBlobUrl)) {
      // Only revoke if it's not the original preview URL
      URL.revokeObjectURL(previewBlobUrl);
    }
    setPreviewBlobUrl(null);
    setTextContent(null);
  };

  const getFileIcon = (type) => {
    if (type.startsWith('image/')) return <Image size={20} color="#3b82f6" />;
    if (type.includes('pdf')) return <FileText size={20} color="#ef4444" />;
    return <File size={20} color="#6b7280" />;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Normalize the template's inputs across both shapes. Legacy
  // templates carry `parameters[]` + `transactions[].is_user_defined`;
  // new-shape templates (template-system-redesign) carry `body.inputs[]`
  // which is already normalized. Returns a unified array used by
  // validation, initial values, change handling, completion detection,
  // and rendering — so the rich UX (completion badges, attachments,
  // validation, styling) works identically for both shapes.
  const getEffectiveInputs = () => {
    if (!entry.templateData) return [];
    // New-shape: body.inputs is already in the unified form.
    if (entry.templateData.body && Array.isArray(entry.templateData.body.inputs)) {
      return entry.templateData.body.inputs;
    }
    // Legacy shape: combine parameters[] + user-defined transactions.
    const out = [];
    if (Array.isArray(entry.templateData.parameters)) {
      for (const p of entry.templateData.parameters) {
        let type = 'text';
        if (p.parameter_type === 'Number') type = 'currency';
        else if (p.parameter_type === 'Date') type = 'date';
        out.push({
          name: p.parameter_name,
          label: p.parameter_label,
          type,
          required: !!p.required,
        });
      }
    }
    if (Array.isArray(entry.templateData.transactions)) {
      entry.templateData.transactions
        .filter((t) => t.is_user_defined)
        .forEach((t, index) => {
          out.push({
            name: `user_account_${index}`,
            label: `Välj konto ${t.debit_formula ? '(Debet)' : '(Kredit)'}`,
            type: 'account_picker',
            required: true,
          });
        });
    }
    return out;
  };

  const isNumericType = (t) =>
    t === 'currency_gross' || t === 'currency_net' || t === 'currency'
    || t === 'number' || t === 'integer';

  // Enhanced validation schema
  const getValidationSchema = () => {
    const baseSchema = {
      description: Yup.string().min(3, "Beskrivningen måste vara minst 3 tecken").required("Beskrivning krävs"),
      date: Yup.date().required("Datum krävs"),
      reference: Yup.string(),
      notes: Yup.string(),
    };

    for (const input of getEffectiveInputs()) {
      let rule;
      if (isNumericType(input.type)) {
        rule = Yup.number();
        if (input.required) {
          rule = rule.min(0.01, `${input.label} måste vara större än 0`);
        }
      } else {
        rule = Yup.string();
      }
      if (input.required) rule = rule.required(`${input.label} krävs`);
      baseSchema[input.name] = rule;
    }

    return Yup.object(baseSchema);
  };

  const getInitialValues = () => {
    const initialValues = {
      description: entry.description || entry.templateData?.template_name || "",
      date: entry.date ? format(entry.date, "yyyy-MM-dd") : format(new Date(), "yyyy-MM-dd"),
      reference: entry.reference || "",
      notes: entry.notes || "",
    };

    // Add parameter values
    if (entry.parameters) {
      Object.keys(entry.parameters).forEach(key => {
        initialValues[key] = entry.parameters[key];
      });
    }

    // Add user-defined account values
    if (entry.userDefinedAccounts) {
      Object.keys(entry.userDefinedAccounts).forEach(key => {
        initialValues[key] = entry.userDefinedAccounts[key];
      });
    }

    return initialValues;
  };

  const handleValuesChange = useCallback((values) => {
    const updatedEntry = {
      ...entry,
      description: values.description,
      date: new Date(values.date),
      reference: values.reference,
      notes: values.notes,
      parameters: {},
      userDefinedAccounts: {}
    };

    const isLegacy = !!(entry.templateData && entry.templateData.parameters);
    for (const input of getEffectiveInputs()) {
      if (values[input.name] === undefined) continue;
      // Legacy split: parameter_name → parameters; user_account_N →
      // userDefinedAccounts. New shape: everything in parameters
      // (account_picker / select inputs share the same bucket).
      if (isLegacy && input.name.startsWith('user_account_')) {
        updatedEntry.userDefinedAccounts[input.name] = values[input.name];
      } else {
        updatedEntry.parameters[input.name] = values[input.name];
      }
    }

    onChange(updatedEntry);
  }, [entry, onChange]);

  // Check if section is completed. "parameters" now covers all
  // template inputs (legacy params + user-defined accounts) since the
  // unified rendering puts them in one section.
  const isSectionCompleted = (sectionName, values, errors) => {
    switch (sectionName) {
      case 'basic':
        return values.description && values.date && !errors.description && !errors.date;
      case 'parameters':
        return getEffectiveInputs().every((input) =>
          (!input.required || values[input.name]) && !errors[input.name]
        );
      default:
        return false;
    }
  };

  if (accountsLoading) {
    return (
      <div className="enhanced-step-content">
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              margin: '0 auto 1rem',
              width: '40px',
              height: '40px',
              border: '4px solid #f3f3f3',
              borderTop: '4px solid #3b82f6',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            <p style={{ color: '#6b7280' }}>Laddar konton...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .enhanced-step-content {
          padding: 1rem;
        }
        
        .info-card {
          background: var(--rgba-primary-1);
          border: 1px solid var(--rgba-primary-2);
          border-radius: 8px;
          padding: 1rem;
          display: flex;
          align-items: flex-start;
          gap: 0.75rem;
          margin-bottom: 1.5rem;
        }
        
        .info-icon {
          color: var(--bs-primary);
          flex-shrink: 0;
          margin-top: 0.125rem;
        }
        
        .info-text {
          color: var(--primary-dark);
          font-size: 0.875rem;
          line-height: 1.5;
          margin: 0;
        }
        
        .verification-section {
          background: #f8fafc;
          border-radius: 8px;
          padding: 1rem;
          margin-bottom: 1rem;
          border: 1px solid #e2e8f0;
          position: relative;
          transition: all 0.2s ease;
        }
        
        .verification-section.completed {
          border-color: var(--bs-primary);
          box-shadow: 0 4px 12px var(--rgba-primary-1);
        }
        
        .verification-section:hover {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
        }
        
        .section-icon {
          width: 16px;
          height: 16px;
          color: #64748b;
        }
        
        .completion-badge {
          position: absolute;
          top: 1rem;
          right: 1rem;
          background: var(--bs-primary);
          color: white;
          padding: 0.25rem 0.5rem;
          border-radius: 12px;
          font-size: 0.75rem;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }
        
        .upload-cards-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
          gap: 0.75rem;
          margin-bottom: 1rem;
        }
        
        .upload-card {
          background: white;
          border: 2px dashed #d1d5db;
          border-radius: 12px;
          padding: 1rem;
          aspect-ratio: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
          position: relative;
          min-height: 100px;
        }
        
        .upload-card.empty {
          border-style: dashed;
          background: #f9fafb;
        }
        
        .upload-card.empty:hover {
          border-color: var(--bs-primary);
          background: var(--rgba-primary-1);
          transform: translateY(-2px);
          box-shadow: 0 4px 12px var(--rgba-primary-2);
        }
        
        .upload-card.empty.drag-over {
          border-color: var(--bs-primary);
          background: var(--rgba-primary-1);
          transform: scale(1.05);
        }
        
        .upload-card.uploaded {
          border: 1px solid #e2e8f0;
          border-style: solid;
          background: white;
        }
        
        .upload-card.uploaded:hover {
          border-color: var(--bs-primary);
          box-shadow: 0 4px 12px var(--rgba-primary-2);
          transform: translateY(-2px);
        }
        
        .upload-card.uploading {
          border: 1px solid var(--bs-primary);
          background: var(--rgba-primary-1);
          cursor: not-allowed;
        }
        
        .upload-card-plus {
          color: #9ca3af;
          margin-bottom: 0.5rem;
          transition: color 0.3s ease;
        }
        
        .upload-card.empty:hover .upload-card-plus {
          color: var(--bs-primary);
        }
        
        .upload-card-text {
          font-size: 0.75rem;
          font-weight: 500;
          color: #6b7280;
          text-align: center;
          transition: color 0.3s ease;
        }
        
        .upload-card.empty:hover .upload-card-text {
          color: var(--bs-primary);
        }
        
        .upload-card-preview {
          width: 100%;
          height: 60px;
          object-fit: cover;
          border-radius: 6px;
          margin-bottom: 0.5rem;
        }
        
        .upload-card-icon {
          width: 40px;
          height: 40px;
          background: #f3f4f6;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 0.5rem;
        }
        
        .upload-card-info {
          text-align: center;
          width: 100%;
        }
        
        .upload-card-name {
          font-size: 0.7rem;
          font-weight: 600;
          color: #374151;
          margin-bottom: 0.125rem;
          word-break: break-word;
          line-height: 1.2;
        }
        
        .upload-card-size {
          font-size: 0.625rem;
          color: #9ca3af;
        }
        
        .upload-card-remove {
          position: absolute;
          top: 0.25rem;
          right: 0.25rem;
          background: var(--bs-danger);
          color: white;
          border: none;
          border-radius: 50%;
          width: 20px;
          height: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          opacity: 0;
          transition: opacity 0.2s ease;
          font-size: 0.7rem;
        }
        
        .upload-card.uploaded:hover .upload-card-remove {
          opacity: 1;
        }
        
        .attachment-stats {
          background: var(--rgba-primary-1);
          border: 1px solid var(--rgba-primary-2);
          border-radius: 8px;
          padding: 1rem;
          margin-bottom: 1rem;
        }
        
        .upload-progress {
          background: var(--rgba-primary-1);
          border: 1px solid var(--rgba-primary-3);
          border-radius: 8px;
          padding: 0.75rem;
          margin-bottom: 1rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        
        .upload-progress .spinner-border {
          width: 1rem;
          height: 1rem;
          border-width: 2px;
        }

        /* Hover Preview Styles */
        .attachment-hover-preview {
          position: fixed;
          z-index: 10000;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
          padding: 16px;
          max-width: 450px;
          max-height: 500px;
          pointer-events: none;
          transform: translateX(0) translateY(-50%);
        }
        
        .preview-header {
          font-size: 12px;
          font-weight: 600;
          color: #374151;
          margin-bottom: 8px;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        
        .preview-content {
          position: relative;
          overflow: hidden;
          border-radius: 6px;
          background: #f8fafc;
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 180px;
        }
        
        .preview-image {
          max-width: 100%;
          max-height: 400px;
          object-fit: contain;
          border-radius: 4px;
        }
        
        .preview-pdf-container {
          width: 100%;
          height: 320px;
          border: 1px solid #e2e8f0;
          border-radius: 4px;
          background: white;
        }
        
        .preview-pdf-iframe {
          width: 100%;
          height: 100%;
          border: none;
          border-radius: 4px;
        }
        
        .preview-text-container {
          width: 100%;
          max-height: 300px;
          overflow-y: auto;
          border: 1px solid #e2e8f0;
          border-radius: 4px;
          background: white;
        }
        
        .preview-text-content {
          padding: 12px;
          margin: 0;
          font-size: 12px;
          line-height: 1.5;
          font-family: 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, 'Courier New', monospace;
        }

        .preview-placeholder {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          color: #6b7280;
          font-size: 14px;
        }
      `}</style>

      <div className="enhanced-step-content">
        <div className="row">
          {/* Main Form Section - Narrower */}
          <div className="col-5">
            <Formik
              innerRef={formikRef}
              initialValues={getInitialValues()}
              validationSchema={getValidationSchema()}
              onSubmit={() => { }}
              enableReinitialize={true}
            >
              {({ values, setFieldValue, errors, touched }) => (
                <Form>
                  {/* Basic Information Section */}
                  <div className={`verification-section ${isSectionCompleted('basic', values, errors) ? 'completed' : ''}`}>
                    {isSectionCompleted('basic', values, errors) && (
                      <div className="completion-badge">
                        <CheckCircle size={12} />
                        Klar
                      </div>
                    )}

                    <div className="d-flex align-items-center mb-3">
                      <MessageSquare className="section-icon me-2" />
                      <h6 className="mb-0 fw-medium">Grundläggande Information</h6>
                    </div>

                    <div className="mb-3">
                      <label htmlFor="description" className="form-label">Beskrivning *</label>
                      <Field
                        type="text"
                        className={`form-control ${errors.description && touched.description ? 'is-invalid' : ''}`}
                        name="description"
                        placeholder={entry.templateData?.template_name || "Vad gäller denna transaktion?"}
                        onChange={(e) => {
                          setFieldValue("description", e.target.value);
                          handleValuesChange({ ...values, description: e.target.value });
                        }}
                      />
                      <ErrorMessage name="description" component="div" className="invalid-feedback" />
                      {entry.templateData?.template_name && (
                        <div className="form-text">
                          Standard: {entry.templateData.template_name}
                        </div>
                      )}
                    </div>

                    <div className="row g-3">
                      <div className="col-sm-6">
                        <label htmlFor="date" className="form-label">Transaktionsdatum *</label>
                        <Field
                          type="date"
                          className={`form-control ${errors.date && touched.date ? 'is-invalid' : ''}`}
                          name="date"
                          onChange={(e) => {
                            setFieldValue("date", e.target.value);
                            handleValuesChange({ ...values, date: e.target.value });
                          }}
                        />
                        <ErrorMessage name="date" component="div" className="invalid-feedback" />
                      </div>

                      <div className="col-sm-6">
                        <label htmlFor="reference" className="form-label">Referens</label>
                        <Field
                          type="text"
                          className="form-control"
                          name="reference"
                          placeholder="Fakturanr, kvittonr, etc."
                          onChange={(e) => {
                            setFieldValue("reference", e.target.value);
                            handleValuesChange({ ...values, reference: e.target.value });
                          }}
                        />
                        <div className="form-text">Valfritt referensnummer</div>
                      </div>
                    </div>
                  </div>

                  {/* Transaktionsdetaljer — unified inputs section.
                      Handles both legacy parameters[]+user-defined
                      transactions and new-shape body.inputs via
                      getEffectiveInputs(). Each input renders the
                      right widget by type. */}
                  {getEffectiveInputs().length > 0 && (
                    <div className={`verification-section ${isSectionCompleted('parameters', values, errors) ? 'completed' : ''}`}>
                      {isSectionCompleted('parameters', values, errors) && (
                        <div className="completion-badge">
                          <CheckCircle size={12} />
                          Klar
                        </div>
                      )}

                      <div className="d-flex align-items-center mb-3">
                        <DollarSign className="section-icon me-2" />
                        <h6 className="mb-0 fw-medium">Transaktionsdetaljer</h6>
                      </div>

                      <div className="row g-3">
                        {getEffectiveInputs().map((input) => {
                          const fieldName = input.name;
                          const isInvalid = errors[fieldName] && touched[fieldName];
                          const reqMark = input.required ? ' *' : '';

                          // Account picker — react-select against the
                          // tenant chart of accounts, optionally
                          // narrowed by input.filter (e.g. class:4-6).
                          if (input.type === 'account_picker') {
                            const filtered = filterAccountsByExpression(accounts, input.filter);
                            return (
                              <div key={fieldName} className="col-12">
                                <label htmlFor={fieldName} className="form-label">
                                  {input.label}{reqMark}
                                </label>
                                <Select
                                  name={fieldName}
                                  value={filtered.find((acc) => acc.value === values[fieldName]) || null}
                                  onChange={(opt) => {
                                    const v = opt ? opt.value : '';
                                    setFieldValue(fieldName, v);
                                    handleValuesChange({ ...values, [fieldName]: v });
                                  }}
                                  options={filtered}
                                  placeholder="Sök konto efter nummer eller namn…"
                                  isClearable
                                  menuPortalTarget={document.body}
                                  styles={{
                                    control: (base) => ({
                                      ...base,
                                      borderColor: isInvalid ? '#dc3545' : base.borderColor,
                                      '&:hover': { borderColor: isInvalid ? '#dc3545' : base.borderColor },
                                    }),
                                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                  }}
                                />
                                <ErrorMessage name={fieldName} component="div" className="text-danger small mt-1" />
                              </div>
                            );
                          }

                          // Select — react-select with the input's
                          // declared options. Used by new-shape
                          // motkonto / intäktskonto / vat-rate
                          // selects.
                          if (input.type === 'select') {
                            const opts = (input.options || []).map((o) => ({ value: o.value, label: o.label }));
                            return (
                              <div key={fieldName} className="col-12">
                                <label htmlFor={fieldName} className="form-label">
                                  {input.label}{reqMark}
                                </label>
                                <Select
                                  name={fieldName}
                                  value={opts.find((o) => o.value === values[fieldName]) || null}
                                  onChange={(opt) => {
                                    const v = opt ? opt.value : '';
                                    setFieldValue(fieldName, v);
                                    handleValuesChange({ ...values, [fieldName]: v });
                                  }}
                                  options={opts}
                                  placeholder="Välj…"
                                  isClearable={!input.required}
                                  menuPortalTarget={document.body}
                                  styles={{
                                    control: (base) => ({
                                      ...base,
                                      borderColor: isInvalid ? '#dc3545' : base.borderColor,
                                      '&:hover': { borderColor: isInvalid ? '#dc3545' : base.borderColor },
                                    }),
                                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                  }}
                                />
                                <ErrorMessage name={fieldName} component="div" className="text-danger small mt-1" />
                              </div>
                            );
                          }

                          // Numeric / text / date inputs — Field with
                          // appropriate type. Hint text below for
                          // currency_gross/_net + integer rate inputs.
                          const fieldType = isNumericType(input.type) ? 'number'
                            : input.type === 'date' ? 'date'
                            : 'text';
                          const step = isNumericType(input.type)
                            ? (input.type === 'integer' ? '1' : '0.01')
                            : undefined;
                          return (
                            <div key={fieldName} className="col-sm-12">
                              <label htmlFor={fieldName} className="form-label">
                                {input.label}{reqMark}
                              </label>
                              <Field
                                type={fieldType}
                                step={step}
                                className={`form-control ${isInvalid ? 'is-invalid' : ''}`}
                                name={fieldName}
                                placeholder={isNumericType(input.type) ? '0,00' : `Ange ${input.label}`}
                                onChange={(e) => {
                                  const raw = e.target.value;
                                  const value = isNumericType(input.type)
                                    ? (raw === '' ? '' : (input.type === 'integer' ? parseInt(raw, 10) : parseFloat(raw)))
                                    : raw;
                                  setFieldValue(fieldName, value);
                                  handleValuesChange({ ...values, [fieldName]: value });
                                }}
                              />
                              <ErrorMessage name={fieldName} component="div" className="invalid-feedback" />
                              {vatRateHint(input) && (
                                <div className="form-text">{vatRateHint(input)}</div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Notes Section */}
                  <div className="verification-section">
                    <div className="d-flex align-items-center mb-3">
                      <MessageSquare className="section-icon me-2" />
                      <h6 className="mb-0 fw-medium">Ytterligare Anteckningar</h6>
                    </div>

                    <Field
                      as="textarea"
                      className="form-control"
                      name="notes"
                      rows="3"
                      placeholder="Eventuell ytterligare information om denna transaktion..."
                      onChange={(e) => {
                        setFieldValue("notes", e.target.value);
                        handleValuesChange({ ...values, notes: e.target.value });
                      }}
                    />
                    <div className="form-text">Lägg till kommentarer, bakgrundsinformation eller andra anteckningar</div>
                  </div>
                </Form>
              )}
            </Formik>
          </div>

          {/* Attachments Section - Wider */}
          <div className="col-lg-7">
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                <h6 className="fw-medium mb-0">Bilagor ({attachments.length}/{maxFiles})</h6>
                <div className="d-flex align-items-center gap-2">
                  {uploadingFiles.size > 0 && (
                    <div className="spinner-border spinner-border-sm text-primary" role="status">
                      <span className="visually-hidden">Laddar upp...</span>
                    </div>
                  )}
                  {attachments.length > 0 && (
                    <span className="badge bg-primary">{attachments.length}</span>
                  )}
                </div>
              </div>

              {/* Upload progress indicator */}
              {uploadingFiles.size > 0 && (
                <div className="upload-progress">
                  <div className="spinner-border spinner-border-sm text-primary" role="status"></div>
                  <div>
                    Laddar upp {uploadingFiles.size} fil{uploadingFiles.size > 1 ? 'er' : ''}...
                  </div>
                </div>
              )}

              {/* Attachment Stats */}
              {attachments.length > 0 && (
                <div className="attachment-stats">
                  <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#0c4a6e', marginBottom: '0.5rem' }}>
                    <FileText size={16} className="me-1" />
                    {attachments.length} fil{attachments.length > 1 ? 'er' : ''} uppladdad{attachments.length > 1 ? 'e' : ''}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#075985' }}>
                    Total storlek: {formatFileSize(attachments.reduce((total, att) => total + att.size, 0))}
                  </div>
                </div>
              )}

              {/* Upload Cards Grid */}
              <div className="upload-cards-grid">
                {/* Existing uploaded files */}
                {attachments.map((attachment) => (
                  <div
                    key={attachment.id}
                    className="upload-card uploaded"
                    onMouseEnter={(e) => handleAttachmentHover(attachment, e)}
                    onMouseLeave={handleAttachmentLeave}
                    style={{ position: 'relative', cursor: 'pointer' }}
                  >
                    <button
                      type="button"
                      className="upload-card-remove"
                      onClick={() => removeAttachment(attachment.id)}
                      title="Ta bort fil"
                    >
                      <X size={12} />
                    </button>

                    {attachment.preview ? (
                      <img
                        src={attachment.preview}
                        alt={attachment.name}
                        className="upload-card-preview"
                      />
                    ) : (
                      <div className="upload-card-icon">
                        {getFileIcon(attachment.type)}
                      </div>
                    )}

                    <div className="upload-card-info">
                      <div className="upload-card-name">
                        {attachment.name.length > 12
                          ? attachment.name.substring(0, 9) + '...'
                          : attachment.name}
                      </div>
                      <div className="upload-card-size">
                        {formatFileSize(attachment.size)}
                      </div>
                    </div>
                  </div>
                ))}

                {/* Upload cards - show based on remaining slots */}
                {Array.from({ length: Math.min(1, maxFiles - attachments.length) }, (_, index) => (
                  <div
                    key={`upload-${index}`}
                    className={`upload-card empty ${isDragOver ? 'drag-over' : ''}`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <div className="upload-card-plus">
                      <Plus size={20} />
                    </div>
                    <div className="upload-card-text">
                      {index === 0 ? 'Ladda upp' : 'Lägg till'}
                    </div>
                  </div>
                ))}
              </div>

              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                style={{ display: 'none' }}
                accept="image/*,.pdf,.doc,.docx,.txt"
                onChange={(e) => handleFileUpload(e.target.files)}
              />

              {/* File limit warning */}
              {attachments.length >= maxFiles && (
                <div className="alert alert-warning mt-3">
                  <Info size={16} className="me-2" />
                  Du har uppnått gränsen på {maxFiles} filer. Ta bort en fil för att ladda upp en ny.
                </div>
              )}

              {/* Tips */}
              {/*<div style={{
                background: '#f0f9ff',
                border: '1px solid #bae6fd',
                borderRadius: '8px',
                padding: '1rem',
                marginTop: '1.5rem'
              }}>
                <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#0c4a6e', marginBottom: '0.5rem' }}>
                  <Info size={16} className="me-1" />
                  Tips för bilagor:
                </div>
                <ul style={{ fontSize: '0.75rem', color: '#075985', margin: 0, paddingLeft: '1.2rem', lineHeight: 1.4 }}>
                  <li>Ladda upp kvitton och fakturor för bättre dokumentation</li>
                  <li>Ta foton med mobilen för snabb uppladdning</li>
                  <li>PDF-filer rekommenderas för viktig dokumentation</li>
                  <li>Du kan ladda upp max {maxFiles} filer per verifikation</li>
                </ul>
              </div>*/}
            </div>
          </div>
        </div>
      </div>

      {/* Hover Preview Window */}
      {hoveredAttachment && (
        <div
          className="attachment-hover-preview"
          style={{
            position: 'fixed',
            left: `${previewPosition.x}px`,
            top: `${previewPosition.y}px`,
            backgroundColor: 'white',
            border: '2px solid #e5e7eb',
            borderRadius: '12px',
            padding: '16px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            zIndex: 10000,
            maxWidth: '400px',
            maxHeight: '500px',
            overflow: 'hidden',
            pointerEvents: 'none'
          }}
        >
          <div className="preview-header" style={{
            fontSize: '14px',
            fontWeight: '600',
            marginBottom: '12px',
            color: '#374151',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Search size={14} className="magnifying-icon" />
            {hoveredAttachment.name}
          </div>

          <div className="preview-content">
            {hoveredAttachment.type?.startsWith('image/') ? (
              loadingPreview ? (
                <div className="preview-placeholder" style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '200px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb'
                }}>
                  <div className="spinner-border spinner-border-sm mb-2" role="status">
                    <span className="visually-hidden">Laddar...</span>
                  </div>
                  <div>Laddar bild...</div>
                </div>
              ) : !previewBlobUrl ? (
                <div className="preview-placeholder" style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '200px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  color: '#6b7280'
                }}>
                  <Image size={32} className="mb-2" />
                  <div>Kunde inte ladda bild</div>
                </div>
              ) : (
                <img
                  src={previewBlobUrl}
                  alt={hoveredAttachment.name}
                  className="preview-image"
                  style={{
                    maxWidth: '350px',
                    maxHeight: '400px',
                    objectFit: 'contain',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb'
                  }}
                  onLoad={(e) => {
                    console.log('Image loaded successfully from blob:', e.target.src);
                  }}
                  onError={(e) => {
                    console.error('Image failed to load from blob:', e.target.src);
                  }}
                />
              )
            ) : hoveredAttachment.type === 'application/pdf' ? (
              loadingPreview ? (
                <div className="preview-placeholder" style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '200px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb'
                }}>
                  <div className="spinner-border spinner-border-sm mb-2" role="status">
                    <span className="visually-hidden">Laddar...</span>
                  </div>
                  <div>Laddar PDF...</div>
                </div>
              ) : !previewBlobUrl ? (
                <div className="preview-placeholder" style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '200px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  color: '#6b7280'
                }}>
                  <FileText size={32} className="mb-2" />
                  <div>Kunde inte ladda PDF</div>
                  <div className="small mt-1">Klicka för att ladda ner</div>
                </div>
              ) : (
                <div className="preview-pdf-container">
                  <iframe
                    src={`${previewBlobUrl}#toolbar=0&navpanes=0&scrollbar=0&statusbar=0&messages=0&scrollbar=0&view=FitH`}
                    title={hoveredAttachment.name}
                    className="preview-pdf-iframe"
                    style={{
                      width: '350px',
                      height: '400px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px'
                    }}
                    onLoad={() => {
                      console.log('PDF loaded successfully in iframe');
                    }}
                    onError={() => {
                      console.error('PDF failed to load in iframe');
                    }}
                  />
                </div>
              )
            ) : hoveredAttachment.type?.startsWith('text/') ? (
              loadingPreview ? (
                <div className="preview-placeholder" style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '200px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb'
                }}>
                  <div className="spinner-border spinner-border-sm mb-2" role="status">
                    <span className="visually-hidden">Laddar...</span>
                  </div>
                  <div>Laddar textfil...</div>
                </div>
              ) : !textContent ? (
                <div className="preview-placeholder" style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '200px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  color: '#6b7280'
                }}>
                  <FileText size={32} className="mb-2" />
                  <div>Kunde inte ladda textfil</div>
                  <div className="small mt-1">Klicka för att ladda ner</div>
                </div>
              ) : (
                <div className="preview-text-container" style={{
                  maxHeight: '300px',
                  overflow: 'auto'
                }}>
                  <pre className="preview-text-content" style={{
                    fontSize: '12px',
                    backgroundColor: '#f9fafb',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                    whiteSpace: 'pre-wrap',
                    margin: 0
                  }}>
                    {textContent.substring(0, 500)}{textContent.length > 500 ? '...' : ''}
                  </pre>
                </div>
              )
            ) : (
              <div className="preview-placeholder" style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '200px',
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                color: '#6b7280'
              }}>
                <File size={32} className="mb-2" />
                <div>{hoveredAttachment.name}</div>
                <div className="small mt-1">Klicka för att ladda ner</div>
              </div>
            )}
          </div>

          <div style={{
            fontSize: '12px',
            color: '#6b7280',
            marginTop: '12px',
            paddingTop: '8px',
            borderTop: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between'
          }}>
            <span>Typ: {hoveredAttachment.type || 'Okänd'}</span>
            <span>Storlek: {(hoveredAttachment.size / 1024).toFixed(1)} KB</span>
          </div>
        </div>
      )}
    </>
  );
};

export default DetailsEntryStep;
